import { ActionIcon, Badge, Box, Button, Checkbox, Grid, Group, Loader, Paper, Switch, Table, Text, Title, Tooltip } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import React, { useEffect } from 'react';
import { FaClock, FaSearchLocation, FaTimes, FaTimesCircle } from 'react-icons/fa';
import { DOWNLOAD_REPORT } from '../../services/reports';
import { GET_ALL_VEHICLES, GET_VEHICLE_TRAJECT } from '../../services/vehicles';
import InputField, { InputFieldTypes } from '../input_field';
import moment from 'moment';
import { GET_COMPANY_INTEGRATION, SET_COMPANY_INTEGRATION } from '../../services/companies';
import { createMarkerIcon, formatDuration, groupBy } from '../../utility/util';
import TrajectView from '../traject_view';
import { Buffer } from 'buffer';
import { useMediaQuery } from '@mantine/hooks';

export default function VehicleTraject({ onGenerate }){
    const [loadingVehicles, setLoadingVehicles] = React.useState<boolean>(false);
    const [loadingGenerate, setLoadingGenerate] = React.useState<boolean>(false);
    const [typeExport, setTypeExport] = React.useState<"details" | "general">("details");
    const [generalFilter, setGeneralFilter] = React.useState<any>({
        period: [moment().startOf("month").toDate(), moment().endOf("month").toDate()],
    });
    const [distanceType, setDistanceType] = React.useState<string>("hodometer");
    const [orientation, setOrientation] = React.useState<string>("portrait");
    const [vehicles, setVehicles] = React.useState<any[]>([]);
    const [selecteds, setSelecteds] = React.useState<string[]>([]);
    const [vehiclePeriods, setVehiclePeriods] = React.useState<any>({});
    const [expandLocation, setExpandLocation] = React.useState<any>(null);
    const [periods, setPeriods] = React.useState<any[]>([{},{},{},{}]);
    const [includesDates, setIncludesDates] = React.useState<any[]>([]);
    const [selectedDate, setSelectedDate] = React.useState<any>(moment().startOf("day").format("YYYY-MM-DD"));
    
    const loadVehicles = () => {
        setLoadingVehicles(true)
        GET_ALL_VEHICLES()
        .then(res => {
            setVehicles(res);
            setLoadingVehicles(false);
        })
        .catch(err => {
            setLoadingVehicles(false)
            showNotification({title: "Ops", message: err.message, color: 'red'})
        })
    }

    const generate = () => {
        setLoadingGenerate(true)
        let vps = {};
        for(let date in vehiclePeriods){
            if(
                typeExport === "general"
                ? moment(date).startOf("day").add(1, "hours").isBetween(moment(generalFilter.period[0]).startOf("day"), moment(generalFilter.period[1]).endOf("day"))
                : includesDates.includes(date)
            ){
                let dt = {};
                for(let v in vehiclePeriods[date]){
                    if(selecteds.includes(v)){ dt[v] = vehiclePeriods[date][v]; }
                }

                vps[date] = dt;
            }
        }
        DOWNLOAD_REPORT("vehicle-traject", {
            periods,
            vehicles: vps,
            distance: distanceType,
            orientation,
            timezone: moment().format(),
        })
        .then(res => {
            setLoadingGenerate(false);
            onGenerate && onGenerate(res);
            showNotification({title: "Relatório socilitado", message: "Aguarde enquanto geramos seu relatório", color: 'yellow'});
        })
        .catch(err => {
            setLoadingGenerate(false)
            showNotification({message: err.message, color: 'red'});
        })
    }

    const loadCache = () => {
        GET_COMPANY_INTEGRATION(`vehicle_traject_${moment(selectedDate).format("YYYY_MM_DD")}`)
        .then(({ vehicles, periods }) => {
            setVehiclePeriods(vp => ({
                ...vp,
                [moment(selectedDate).format("YYYY-MM-DD")]: {...vehicles},
            }));
            setPeriods(p => periods ?? [{name: "Intervalo 1"},{name: "Intervalo 2"},{name: "Intervalo 3"},{name: "Intervalo 4"}]);
        })
        .catch(err => {
            showNotification({title: "Ops.", message: err.message, color: 'red'})
        })
    }
    
    const saveCache = () => {
        SET_COMPANY_INTEGRATION(`vehicle_traject_${moment(selectedDate).format("YYYY_MM_DD")}`, {
            vehicles: vehiclePeriods[moment(selectedDate).format("YYYY-MM-DD")],
            periods,
        })
        .then((res) => {
            showNotification({message: "Períodos salvos"})
        })
        .catch(err => {})
    }

    useEffect(() => {
        loadCache()
    }, [selectedDate]);

    useEffect(() => {
        if(selectedDate){
            setIncludesDates(sd => groupBy([
                ...sd.filter(item => item !== moment(selectedDate).format("YYYY-MM-DD")),
                moment(selectedDate).format("YYYY-MM-DD")
            ], item => item).map(g => g[1][0]));
        }
    }, [selectedDate]);

    useEffect(loadVehicles, []);

    useEffect(() => {
        if(typeExport === "general"){
            if(generalFilter.period[0] && generalFilter.period[1]){
                setPeriods([{name: "Dia inteiro"}])
                setVehiclePeriods(() => {
                    let res : any = {};
                    let d = moment(generalFilter.period[0]);
                    while(d.format() <= moment(generalFilter.period[1]).format()){
                        res[d.startOf("day").format("YYYY-MM-DD")] = {};
                        for(let v of vehicles){
                            res[d.startOf("day").format("YYYY-MM-DD")][v._id] = [{
                                start: "00:00",
                                finish: "23:59",
                            }]
                        }
                        d.add(1, "days");
                    }
                    return res;
                })
            }
        }
    }, [generalFilter, typeExport, vehicles]);

    const typeExportInput = <InputField
        title="Formato"
        fieldType={InputFieldTypes.SELECT}
        name="vl"
        value={typeExport}
        options={[
            {label: "Geral", value: "general"},
            {label: "Detalhado", value: "details"},
        ]}
        onChange={({vl}) => setTypeExport(vl)}
    />

    return <div>
        {loadingVehicles && <Loader />}

        { typeExport === "general"
        ? <Grid align="flex-start">
            <Grid.Col xs={12} md={3}>
                {typeExportInput}
            </Grid.Col>
            <Grid.Col xs={12} md={5}>
                <InputField
                    title="Período"
                    fieldType={InputFieldTypes.DATERANGE}
                    name="d"
                    value={generalFilter.period}
                    onChange={({d}) => setGeneralFilter(gf => ({...gf, period: d}))}
                />
            </Grid.Col>
            <Grid.Col xs={12} md={3}>
                {/* <Switch
                    label="Odômetro"
                    checked={distanceType === "hodometer"}
                    onChange={() => { setDistanceType(dt => dt === "hodometer" ? "location" : "hodometer") }}
                />
                <Switch
                    mt="md"
                    label="Horizontal"
                    checked={orientation === "landscape"}
                    onChange={() => { setOrientation(dt => dt === "landscape" ? "portrait" : "landscape") }}
                /> */}
            </Grid.Col>
            {/* <Grid.Col xs={12} md={3}>
                <InputField
                    title="Agrupar por"
                    fieldType={InputFieldTypes.DATERANGE}
                    name="group"
                    value={generalFilter.group}
                    options={[
                        {label: "Mês", value: "month"},
                    ]}
                    onChange={({group}) => setGeneralFilter(gf => ({...gf, group}))}
                />
            </Grid.Col> */}
            <Grid.Col xs={12}>
                <Table striped>
                    <thead>
                        <tr>
                            <th style={{ width: 50 }}><Checkbox
                                checked={vehicles.every(v => selecteds.includes(v._id))}
                                onChange={() => setSelecteds(s => (
                                    vehicles.every(v => s.includes(v._id))
                                    ? []
                                    : vehicles.map(v => v._id)
                                ))} /></th>
                            <th rowSpan={2}>Veículo</th>
                        </tr>
                    </thead>
                    <tbody>
                        {vehicles.map(vehicle => <tr>
                            <td>
                                <Checkbox
                                    checked={selecteds.includes(vehicle._id)}
                                    onChange={() => setSelecteds(s => (
                                        selecteds.includes(vehicle._id)
                                        ? s.filter(it => it !== vehicle._id)
                                        : [...s, vehicle._id]
                                    ))}
                                />
                            </td>
                            <td>{vehicles.find(v => v._id === vehicle._id)?.license_plate}</td>
                            
                        </tr>)}
                    </tbody>
                </Table>

                <Group mt="lg" style={{justifyContent: 'space-between'}} align="flex-end">
                    {/* <Button variant='subtle' color="gray" size="xs" onClick={() => setItems([])}>Limpar tabela</Button> */}
                    <Button variant='filled' color="orange" size="sm" loading={loadingGenerate} onClick={generate}>Gerar relatório</Button>
                </Group>
            </Grid.Col>
        </Grid>
        : <Grid align="flex-start">
            <Grid.Col xs={12} md={3}>
                {typeExportInput}
            </Grid.Col>
            <Grid.Col xs={12} md={3}>
                <InputField
                    title="Data"
                    fieldType={InputFieldTypes.DATE}
                    name="d"
                    value={selectedDate ? moment(selectedDate).toDate() : undefined}
                    onChange={({d}) => setSelectedDate(d ? moment(d).format("YYYY-MM-DD") : null)}
                />
            </Grid.Col>
            <Grid.Col xs={12} md={6}>
                <Text>Datas inseridas ao relatório:</Text>
                {includesDates.sort().map((d) => <Badge size="xl" mr="xs" mb="xs" rightSection={<ActionIcon size="xs"
                    onClick={() => setIncludesDates(id => id.filter(item => item !== d))}
                ><FaTimes /></ActionIcon>}>{moment(d).format("DD/MM/YYYY")}</Badge>)}
            </Grid.Col>
            <Grid.Col xs={12} md={9}>
                {/* <Switch
                    label="Odômetro"
                    checked={distanceType === "hodometer"}
                    onChange={() => { setDistanceType(dt => dt === "hodometer" ? "location" : "hodometer") }}
                />
                <Switch
                    mt="md"
                    label="Horizontal"
                    checked={orientation === "landscape"}
                    onChange={() => { setOrientation(dt => dt === "landscape" ? "portrait" : "landscape") }}
                /> */}
            </Grid.Col>
            <Grid.Col xs={12} md={3}>
                <Button
                    fullWidth
                    size="xs"
                    variant='outline'
                    onClick={() => setPeriods(p => [...p, {}])}
                >Adicionar Intervalo</Button>
            </Grid.Col>
            <Grid.Col xs={12} md={12} style={{overflowX: 'auto'}}>
                <Table
                    striped
                    withBorder
                    align='center'
                >
                    <thead>
                        <tr>
                            <th style={{width: 50}}><Checkbox
                                checked={vehicles.every(v => selecteds.includes(v._id))}
                                onChange={() => setSelecteds(s => (
                                    vehicles.every(v => s.includes(v._id))
                                    ? []
                                    : vehicles.map(v => v._id)
                                ))} /></th>
                            <th style={{textAlign: "center"}} rowSpan={2}>Veículo</th>
                            {periods.map((period, i) => <>
                                <th colSpan={4} style={{textAlign: 'center', borderLeft: '2px solid #CACACA', borderRight: '2px solid #CACACA'}}>
                                    <Group>
                                        <InputField
                                            name="name"
                                            value={![null || undefined].includes(period.name) ? period.name : `Intervalo ${i+1}`}
                                            style={{flex: 1}}
                                            onChange={({name}) => setPeriods(p => p.map((item, j) => i === j ? {...item, name} : item))}
                                        />
                                        <ActionIcon onClick={() => {
                                            setPeriods(p => p.filter((item, j) => i !== j));
                                            setVehiclePeriods(vps => {
                                                let res = {...vps};
                                                for(let date in res){
                                                    for(let key in res[date]){
                                                        if(date === selectedDate){
                                                            res[date][key].splice(i,1);
                                                        }
                                                    }
                                                }
                                                return res;
                                            })
                                        }}><FaTimesCircle /></ActionIcon>
                                    </Group>
                                </th>
                            </>)}
                        </tr>
                    </thead>
                    <tbody>
                        {vehicles.map(vehicle => <tr>
                            <td>
                                <Checkbox
                                    checked={selecteds.includes(vehicle._id)}
                                    onChange={() => setSelecteds(s => (
                                        selecteds.includes(vehicle._id)
                                        ? s.filter(it => it !== vehicle._id)
                                        : [...s, vehicle._id]
                                    ))}
                                />
                            </td>
                            <td style={{textAlign: 'center'}}>{vehicles.find(v => v._id === vehicle._id)?.license_plate}</td>
                            {periods.map((period, index) => <>
                                <td style={{borderLeft: '2px solid #CACACA'}}>
                                    {
                                        ({...{...vehiclePeriods[selectedDate]}[`${vehicle._id}`]}[index]?.start) &&
                                        ({...{...vehiclePeriods[selectedDate]}[`${vehicle._id}`]}[index]?.finish) &&
                                        <Tooltip label="Replicar nos demais">
                                            <ActionIcon size="xs" onClick={() => {
                                                setVehiclePeriods(vps => {
                                                    let res = {...vps};
                                                    for(let date in res){
                                                        for(let vh of vehicles){
                                                            let key = vh._id;
                                                            if(date === selectedDate && key !== vehicle._id){
                                                                const arr = [];
                                                                for(let j = 0 ; j <= index ; j++){
                                                                    arr.push(j === index ? {
                                                                        start: {...{...vehiclePeriods[selectedDate]}[`${vehicle._id}`]}[index]?.start,
                                                                        finish: {...{...vehiclePeriods[selectedDate]}[`${vehicle._id}`]}[index]?.finish,
                                                                    } : null);
                                                                }
                                                                res[date][key] = arr;
                                                            }
                                                        }
                                                    }
                                                    return res;
                                                })
                                            }}><FaClock /></ActionIcon>
                                        </Tooltip>
                                    }
                                </td>
                                <td style={{borderLeft: '2px solid #CACACA'}}>
                                    <InputField
                                        fieldType={InputFieldTypes.TIME}
                                        name="start"
                                        style={{flex: 1}}
                                        value={{...{...vehiclePeriods[selectedDate]}[`${vehicle._id}`]}[index]?.start || " "}
                                        onChange={({start}) => {
                                            setVehiclePeriods(vp => ({...vp, [selectedDate]: {
                                                ...vehiclePeriods[selectedDate],
                                                [`${vehicle._id}`]: 
                                                    periods.map((p, j) => index === j
                                                        ? {...({...vehiclePeriods[selectedDate]}[`${vehicle._id}`] || [])[j], start}
                                                        : ({...vehiclePeriods[selectedDate]}[`${vehicle._id}`] || [])[j]),
                                            }
                                            }))
                                        }}
                                    />
                                </td>
                                <td style={{borderRight: '2px solid #CACACA'}}>
                                    <InputField
                                        fieldType={InputFieldTypes.TIME}
                                        name="finish"
                                        value={{...{...vehiclePeriods[selectedDate]}[`${vehicle._id}`]}[index]?.finish || " "}
                                        onChange={({finish}) => {
                                            setVehiclePeriods(vp => ({...vp, [selectedDate]: {
                                                ...vehiclePeriods[selectedDate],
                                                [`${vehicle._id}`]: 
                                                    periods.map((p, j) => index === j
                                                        ? {...({...vehiclePeriods[selectedDate]}[`${vehicle._id}`] || [])[j], finish}
                                                        : ({...vehiclePeriods[selectedDate]}[`${vehicle._id}`] || [])[j]),
                                            }
                                            }))
                                        }}
                                    />
                                </td>
                                <td style={{borderRight: '2px solid #CACACA'}}>
                                    {
                                        ({...{...vehiclePeriods[selectedDate]}[`${vehicle._id}`]}[index]?.start) &&
                                        ({...{...vehiclePeriods[selectedDate]}[`${vehicle._id}`]}[index]?.finish) &&
                                        <Tooltip label="Ver no mapa">
                                            <ActionIcon size="xs" onClick={() => {
                                                setExpandLocation({
                                                    start: moment(
                                                        `${selectedDate} ${vehiclePeriods[selectedDate][`${vehicle._id}`][index].start}:00`,
                                                        "YYYY-MM-DD HH:mm:ss"
                                                    ).format(),
                                                    finish: moment(
                                                        `${selectedDate} ${vehiclePeriods[selectedDate][`${vehicle._id}`][index].finish}:59`,
                                                        "YYYY-MM-DD HH:mm:ss"
                                                    ).format(),
                                                    vehicle,
                                                })
                                            }}><FaSearchLocation /></ActionIcon>
                                        </Tooltip>
                                    }
                                </td>
                            </>)}
                        </tr>)}
                    </tbody>
                </Table>
            </Grid.Col>
            <Grid.Col xs={12}>
                <Group mt="lg" style={{justifyContent: 'space-between'}} align="flex-end">
                    {/* <Button variant='subtle' color="gray" size="xs" onClick={() => setItems([])}>Limpar tabela</Button> */}
                    <Button variant='subtle' color="gray" size="xs" onClick={saveCache}>Salvar Períodos</Button>
                    <Button variant='filled' color="orange" size="sm" disabled={selecteds.length === 0} loading={loadingGenerate} onClick={generate}>Gerar relatório</Button>
                </Group>
            </Grid.Col>
        </Grid>}

        <TrajectView
            isVisible={!!expandLocation}
            onClose={() => setExpandLocation(null)}
            vehicle={expandLocation?.vehicle}
            data={expandLocation}
        />
    </div>
}